import { HasTip } from '../common/InfoTip';
import './ig-kit-lot55.css';
import React, { useState } from 'react';
import { Info, Settings } from 'lucide-react';
import { IgSheet } from './IgSheet';
import { IgGroupe, IgLigne, IgSwitch } from './IgListe';
import '../../styles/social-ig.css';
import '../../styles/ig-kit.css';
import '../recipes/recettes-ig.css';
import '../fil/fil.css';
import { avecLogos } from '../BrandLogos';
export { IgSheet, IgConfirmProvider, IgVide, confirmer } from './IgSheet';
export { IgGroupe, IgLigne, IgSwitch };

/* Kit « mode Instagram » commun à tous les modules : profil → onglets fins → flux d'une carte par sujet. */

export const useIgTab = (storageKey, defaut, autorises) => {
  const [tab, setTabState] = useState(() => { const t = localStorage.getItem(storageKey); return autorises.includes(t) ? t : defaut; });
  const setTab = (t) => { setTabState(t); localStorage.setItem(storageKey, t); };
  return [tab, setTab];
};

const TONE_COLOR = { danger: '#C92A2A', warn: '#B25900', ok: '#1A7F37', accent: '#7C5CE6' };
const fiabKey = (f) => ({ 'réel': 'reel', 'théorique': 'theorique', 'estimé': 'estime', 'saisi': 'saisi' })[String(f || '').split(' ')[0]] || (String(f || '').startsWith('—') ? 'vide' : 'reel');
export const IgProfil = ({ eyebrow, title, sub, avatar, counters = [], actions = [], aide, testId = 'ig-profil', children }) => {
  const [aideOpen, setAideOpen] = useState(false);
  return (
  <section className="hp-card ig-profile" data-testid={testId}>
    {aide && <><button type="button" className="ig-aide-btn" onClick={() => setAideOpen(true)} aria-label="Aide de la page" title="Aide de la page" data-testid={`${testId}-aide`} data-keepicon=""><Info size={20} /></button>
      <IgSheet open={aideOpen} onClose={() => setAideOpen(false)} title="Aide de la page" testId={`${testId}-aide-sheet`}><div className="ig-aide-corps">{typeof aide === 'string' ? <p>{aide}</p> : aide}</div></IgSheet></>}
    <div className="flex items-start gap-4">
      <div className="ig-avatar" data-testid={`${testId}-avatar`}><span>{avatar}</span></div>
      <div className="min-w-0 flex-1">
        {eyebrow && <p className="ig-eyebrow text-[12px] font-bold uppercase tracking-[0.12em] text-[var(--texte-2)] truncate">{avecLogos(eyebrow, 13)}</p>}
        <h1 className="rc-title rc-title-2l" data-testid={`${testId}-titre`}>{avecLogos(title, 22)}</h1>
        {sub && <p className="ig-profile-sub" data-testid={`${testId}-sous`}>{avecLogos(sub)}</p>}
        {counters.length > 0 && (
          <div className="ig-counters" data-testid={`${testId}-compteurs`}>
            {counters.map((c) => <div key={c.key || c.label} role={c.onClick ? 'button' : undefined} tabIndex={c.onClick ? 0 : undefined} onClick={c.onClick} onKeyDown={c.onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); c.onClick(); } } : undefined} title={c.title} className={c.onClick ? 'is-click' : undefined} data-testid={c.testId}><b data-testid={`${testId}-compteur-${c.key || c.label}`} style={c.color || TONE_COLOR[c.tone] ? { color: c.color || TONE_COLOR[c.tone] } : undefined}>{c.tip ? <HasTip titre={c.label} fiabilite={fiabKey(c.tip.fiab)} source={c.tip.source} periode={c.tip.periode} formule={c.tip.formule} fiabiliteLigne={c.tip.fiab}>{c.value}</HasTip> : c.value}</b><span>{c.label}</span></div>)}
          </div>
        )}
      </div>
    </div>
    {actions.length > 0 && (
      <div className="ig-profile-actions">
        {actions.map(({ label, Icon, onClick, dark, title, testId: t }) => { const I = label ? Icon : Settings; return <button key={label || t} type="button" onClick={onClick} title={title || (label ? undefined : 'Réglages du module')} aria-label={label || title || 'Réglages du module'} className={`btn-kit ${dark ? 'btn-kit-dark' : 'btn-kit-light'} ${label ? '' : 'is-ico is-outils'}`} data-testid={t} data-keepicon={label ? undefined : ''}>{I && <I size={label ? 16 : 18} />}{label ? <> {avecLogos(label)}</> : null}</button>; })}
      </div>
    )}
    {children}
  </section>
  );
};

/* Gabarit unique du menu ⚙ : les actions de la page classées en Importer / Exporter / Gérer / Réglages.
   Chaque action : { groupe, icone, label, valeur, sous, tone, onClick, testId, disabled }. groupe absent → « Réglages ». */
const IG_GABARIT = [['Importer', 'importer'], ['Exporter', 'exporter'], ['Gérer', 'gerer'], ['Réglages', 'reglages']];
export const IgReglages = ({ items = [], testId = 'ig-reglages' }) => {
  const valides = items.filter(Boolean);
  const groupeDe = (o) => (o.groupe && String(o.groupe).trim()) || 'Réglages';
  const connus = IG_GABARIT.map(([g]) => g);
  const extras = [...new Set(valides.map(groupeDe))].filter((g) => !connus.includes(g));
  const ordre = [...IG_GABARIT, ...extras.map((g) => [g, g.toLowerCase().replace(/[^a-z0-9]+/g, '-')])];
  return (
    <div className="ig-reglages" data-testid={testId}>
      {ordre.map(([titre, key]) => {
        const liste = valides.filter((o) => groupeDe(o) === titre);
        if (!liste.length) return null;
        return (
          <div key={key} className="ig-reglages-groupe" data-testid={`${testId}-groupe-${key}`}>
            <p className="ig-reglages-titre">{titre}</p>
            <div className="ig-groupe-c">
              {liste.map((o) => <IgLigne key={o.testId || o.label} icone={o.icone} label={o.label} sous={o.sous} valeur={o.valeur} tone={o.tone} disabled={o.disabled} onClick={o.onClick} testId={o.testId} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const IgOnglets = ({ tabs, tab, onTab, badges = {}, testId = 'ig-onglets' }) => (
  <nav className="ig-tabs" role="tablist" data-testid={testId}>
    {tabs.map(({ id, label, Icon, count }) => (
      <button key={id} type="button" role="tab" aria-selected={id === tab} onClick={() => onTab(id)} className={`ig-tab ${id === tab ? 'is-on' : ''} ${Icon ? '' : 'is-text'}`} data-testid={`${testId}-${id}`}>
        {Icon && <span className="rc-tab-icon"><Icon size={20} data-keepicon />{badges[id] ? <i className="rc-tab-dot" data-testid={`${testId}-dot-${id}`} /> : null}</span>}
        <span>{avecLogos(label, 15)}{count != null && count !== 0 ? <b className="ig-tab-n">{count}</b> : null}</span>
      </button>
    ))}
  </nav>
);

export const IgFeed = ({ children, testId }) => <div className="rc-feed" data-testid={testId}>{children}</div>;

/* Blocs chiffrés IG · calcul (A + B − C = D), cascade, répartition (barre + légende), graphique (titre · légende · conclusion) */
export const igCourt = (n) => (n == null || Number.isNaN(Number(n)) ? '—' : Number(n).toLocaleString('fr-FR', { maximumFractionDigits: 0 }));

export const IgCalcul = ({ termes = [], resultat, unite = '', titre, testId = 'ig-calcul' }) => (
  <section className="ig-calcul" data-testid={testId}>
    {titre && <div className="ig-section"><h4>{titre}</h4></div>}
    <div className="ig-calcul-ligne">
      {termes.map((t, i) => <React.Fragment key={t.key || i}>{i > 0 && <span className="ig-op">{t.op || '+'}</span>}<span className="ig-terme" title={t.exact}><b>{t.valeur}</b><small>{t.label}</small></span></React.Fragment>)}
      <span className="ig-op">=</span>
      <span className={`ig-terme is-resultat ${resultat?.tone ? `tone-${resultat.tone}` : ''}`} title={resultat?.exact} data-testid={`${testId}-resultat`}><b>{resultat?.valeur}{unite && <i> {unite}</i>}</b><small>{resultat?.label}</small></span>
    </div>
  </section>
);

export const IgRepartition = ({ titre, sous, parts = [], total, testId = 'ig-repartition' }) => (
  <section className="ig-repart" data-testid={testId}>
    {titre && <div className="ig-section"><h4>{titre}{sous && <span className="ig-section-sub">{sous}</span>}</h4>{total && <span className="ig-repart-total">{total}</span>}</div>}
    <div className="ig-barre">{parts.map((p) => <span key={p.key || p.label} style={{ width: `${Math.max(0, Math.min(100, p.part * 100))}%`, background: p.color }} title={`${p.label} · ${Math.round(p.part * 100)} %`} />)}</div>
    <ul className="ig-legende">{parts.map((p) => <li key={p.key || p.label}><i style={{ background: p.color }} /><span>{p.label}</span><b>{Math.round(p.part * 100)} %</b>{p.valeur && <small>{p.valeur}</small>}</li>)}</ul>
  </section>
);

export const IgGraphique = ({ titre, legende, conclusion, children, testId = 'ig-graphique' }) => (
  <section className="ig-graph" data-testid={testId}>
    <div className="ig-section"><h4>{titre}</h4>{legende && <span className="ig-graph-legende">{legende}</span>}</div>
    <div className="ig-graph-body">{children}</div>
    {conclusion && <p className="ig-graph-conclusion" data-testid={`${testId}-conclusion`}>{conclusion}</p>}
  </section>
);
